import React, { useState, useRef, useEffect } from 'react';
import { useSport } from '../../contexts/SportContext';
import { useProfile } from '../../contexts/ProfileContext';
import { FEATURES } from '../../config/features';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';


/**
 * SportSelector -- Dropdown to switch active sport.
 * All authenticated users see this. Sports filtered by feature flags.
 * Admin sees all 4 sports. Regular users see feature-flagged sports.
 */
const SportSelector = () => {
  const { isSuperAdmin } = useProfile();
  const { activeSportObj, setActiveSport, sports } = useSport();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Feature-flag filter for non-admin users
  const SPORT_FEATURE_MAP = {
    football: FEATURES.ENABLE_FOOTBALL,
    football_american: FEATURES.ENABLE_AMERICAN_FOOTBALL,
    swimming: FEATURES.ENABLE_SWIMMING,
    triathlon: FEATURES.ENABLE_TRIATHLON,
  };

  const availableSports = isSuperAdmin
    ? sports
    : sports.filter((s) => SPORT_FEATURE_MAP[s.key] !== false);

  if (!availableSports || availableSports.length <= 1) return null;

  return (
    <div ref={ref} className="relative px-3 py-2 border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 transition-all duration-200"
      >
        <span className="text-lg">{activeSportObj?.icon}</span>
        <div className="flex-1 text-left">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: BRAND_GOLD }}>
            Esporte
          </p>
          <p className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>
            {activeSportObj?.label}
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {availableSports.map((sport) => {
            const isActive = sport.key === activeSportObj?.key;
            return (
              <button
                key={sport.key}
                onClick={() => { setActiveSport(sport.key); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                  isActive ? 'bg-green-50 border-l-4' : 'hover:bg-gray-50 border-l-4 border-transparent'
                }`}
                style={isActive ? { borderLeftColor: BRAND_GOLD } : {}}
              >
                <span className="text-lg">{sport.icon}</span>
                <p className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`} style={{ color: BRAND_GREEN }}>
                  {sport.label}
                </p>
                {isActive && (
                  <svg className="w-4 h-4 ml-auto" style={{ color: BRAND_GREEN }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SportSelector;
